import React, { useState, Children, useMemo, useContext } from 'react';
import { func, node, oneOf, object } from 'prop-types';
import cx from 'classnames';
import useStyles from 'hooks/useStyles';

import ApplicationContext from 'components/ApplicationContext';
import TabPane from './components/TabPane';

import styles from './Tabs.scss';

function Tabs({ children, mode, onChange, classNameMap }) {
  useStyles(styles);

  const { query } = useContext(ApplicationContext);

  const tabChildren = useMemo(() => {
    return Children.toArray(children).map(child => ({
      tabKey: child.props.tabKey,
      tabTitle: child.props.tabTitle,
      component: child,
    }));
  }, [children]);

  const getDefaultTab = defaultTabKey =>
    defaultTabKey
      ? tabChildren.find(({ tabKey }) => tabKey === defaultTabKey)
      : null;

  const defaultTabKey = parseInt(query?.tab, 10);

  const [selectedTab = {}, setSelectedTab] = useState(
    getDefaultTab(defaultTabKey) || tabChildren[0],
  );

  const onClickTab = (tab, index) => () => {
    if (tab.tabKey === selectedTab.tabKey) return;

    setSelectedTab(tab);
    if (typeof onChange === 'function') onChange(tab.tabKey, { ...tab, index });
  };

  const selectedChild = useMemo(
    () =>
      Children.toArray(children).find(
        child => child.props.tabKey === selectedTab.tabKey,
      ),
    [selectedTab, children],
  );

  return (
    <div className={cx(styles.root, classNameMap.root)}>
      <div className={cx(styles.tabList, classNameMap.tabList)}>
        {tabChildren.map((tab, index) => {
          const { tabTitle, tabKey } = tab;
          return mode === 'tabs' ? (
            <div
              className={cx(styles.tab, classNameMap.tab, {
                [styles.tabSelected]: selectedTab.tabKey === tabKey,
                [classNameMap.tabSelected]: selectedTab.tabKey === tabKey,
              })}
              key={tabKey}
              onClick={onClickTab(tab, index)}
            >
              {tabTitle}
            </div>
          ) : (
            <a
              className={cx(styles.tab, {
                [styles.tabSelected]: selectedTab.tabKey === tabKey,
                [classNameMap.tabSelected]: selectedTab.tabKey === tabKey,
              })}
              key={tabKey}
              href={`#${tabKey}`}
              onClick={onClickTab(tab, index)}
            >
              {tabTitle}
            </a>
          );
        })}
      </div>
      <div className={cx(styles.container, classNameMap.container)}>
        {mode === 'tabs' ? selectedChild : children}
      </div>
    </div>
  );
}

Tabs.TabPane = TabPane;

Tabs.propTypes = {
  children: node.isRequired,
  onChange: func,
  classNameMap: object,
  mode: oneOf(['tabs', 'anchor']),
};

Tabs.defaultProps = {
  onChange: undefined,
  classNameMap: {},
  mode: 'tabs',
};

export default Tabs;
